import {
  createContext,
  type FunctionComponent,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { ReactConsentContext, useSelectedWorkspace } from '../../Flow/ConsentContextProvider';
import { useAnalytics } from '../../Analytics/AnalyticsProvider';
import { fetchWorkspacePolicyCheck } from '../shared/agent-consent/domain/policyClient';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export const A2A_DOMAIN_STATUS = {
  idle: 'idle',
  loading: 'loading',
  allowed: 'allowed',
  blocked: 'blocked',
  error: 'error',
  a2aRestricted: 'a2aRestricted',
} as const;

export type A2ADomainStatus = (typeof A2A_DOMAIN_STATUS)[keyof typeof A2A_DOMAIN_STATUS];

export const A2A_ERROR_REASONS = {
  httpOrParse: 'httpOrParse',
  a2aRestricted: 'a2aRestricted',
  infraError: 'infraError',
  policyBlocked: 'policyBlocked',
} as const;

export type A2AContext = {
  a2aDomainStatus: A2ADomainStatus;
};

export const ReactA2AContext = createContext<A2AContext | null>(null);

export const useA2AContext = () => {
  const context = useContext(ReactA2AContext);
  if (!context) {
    throw new Error('useA2AContext must be used within A2AContextProvider');
  }
  return context;
};

// ---------------------------------------------------------------------------
// Domain-check state reducer
// ---------------------------------------------------------------------------

type DomainState = { status: A2ADomainStatus };

type DomainAction =
  | { type: 'RESET' }
  | { type: 'LOADING' }
  | { type: 'ALLOWED' }
  | { type: 'BLOCKED' }
  | { type: 'ERROR' }
  | { type: 'A2A_RESTRICTED' };

function domainReducer(_: DomainState, action: DomainAction): DomainState {
  switch (action.type) {
    case 'RESET':          return { status: A2A_DOMAIN_STATUS.idle };
    case 'LOADING':        return { status: A2A_DOMAIN_STATUS.loading };
    case 'ALLOWED':        return { status: A2A_DOMAIN_STATUS.allowed };
    case 'BLOCKED':        return { status: A2A_DOMAIN_STATUS.blocked };
    case 'ERROR':          return { status: A2A_DOMAIN_STATUS.error };
    case 'A2A_RESTRICTED': return { status: A2A_DOMAIN_STATUS.a2aRestricted };
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const A2AContextProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const consentContext = useContext(ReactConsentContext);
  if (!consentContext) {
    throw new Error('A2AContextProvider must be used within ConsentContextProvider');
  }

  const { token } = consentContext;
  const analyticsClient = useAnalytics();
  const { selectedWorkspace } = useSelectedWorkspace();

  const [domainState, dispatch] = useReducer(domainReducer, { status: A2A_DOMAIN_STATUS.idle });

  // Keep the shared acceptDisabled flag in sync with the domain-check status
  // so the Footer Accept button is disabled when the domain is not allowed.
  const { setAcceptDisabled } = consentContext;
  useEffect(() => {
    setAcceptDisabled?.(domainState.status !== A2A_DOMAIN_STATUS.allowed);
  }, [domainState.status, setAcceptDisabled]);

  // Fetch domain check whenever selectedWorkspace changes
  useEffect(() => {
    if (token.serviceType !== 'A2A') return;

    if (!selectedWorkspace) {
      // No workspace selected — either the user hasn't picked one yet
      // or there are none available (empty array / key missing from token).
      // Stay idle so we don't fire a policy fetch that would fail and show
      // a misleading error alongside the SiteSelector's own empty-state.
      dispatch({ type: 'RESET' });
      return;
    }

    const ac = new AbortController();
    dispatch({ type: 'LOADING' });

    void (async () => {
      try {
        const result = await fetchWorkspacePolicyCheck(
          { contextJwt: token.raw, selectedWorkspace },
          ac.signal,
        );

        if (ac.signal.aborted) return;

        if (!result.ok) {
          dispatch({ type: 'ERROR' });
          analyticsClient.pushTrackEvent({
            actionSubject: 'a2aDomainCheck',
            action: 'failed',
            source: 'oauthConsentScreen',
            attributes: { reason: A2A_ERROR_REASONS.httpOrParse },
          });
          return;
        }

        // a2aEnabled=false → A2A is restricted by the organization admin
        if (!result.data.a2aEnabled) {
          dispatch({ type: 'A2A_RESTRICTED' });
          analyticsClient.pushTrackEvent({
            actionSubject: 'a2aDomainCheck',
            action: 'failed',
            source: 'oauthConsentScreen',
            attributes: { reason: A2A_ERROR_REASONS.a2aRestricted },
          });
          return;
        }

        const { domain } = result.data;

        // domain.allowed=false, domain.error=true → infra error → "Try again"
        if (domain.error) {
          dispatch({ type: 'ERROR' });
          analyticsClient.pushTrackEvent({
            actionSubject: 'a2aDomainCheck',
            action: 'failed',
            source: 'oauthConsentScreen',
            attributes: { reason: A2A_ERROR_REASONS.infraError },
          });
          return;
        }

        // domain.allowed=false, domain.error=false → policy blocked → "Contact your admin"
        if (!domain.allowed) {
          dispatch({ type: 'BLOCKED' });
          analyticsClient.pushTrackEvent({
            actionSubject: 'a2aDomainCheck',
            action: 'failed',
            source: 'oauthConsentScreen',
            attributes: { reason: A2A_ERROR_REASONS.policyBlocked },
          });
          return;
        }

        // domain.allowed=true, domain.error=false → ✅ allowed
        dispatch({ type: 'ALLOWED' });
      } catch (e) {
        if ((e instanceof DOMException && e.name === 'AbortError') || ac.signal.aborted) return;
        dispatch({ type: 'ERROR' });
      }
    })();

    return () => { ac.abort(); };
  }, [token, selectedWorkspace, analyticsClient]);

  const value = useMemo<A2AContext>(() => ({
    a2aDomainStatus: domainState.status,
  }), [domainState]);

  return (
    <ReactA2AContext.Provider value={value}>
      {children}
    </ReactA2AContext.Provider>
  );
};

export default A2AContextProvider;
